import type { FormUseRequest } from './validationSchema';
import { useEffect, useState } from 'react';
import { Controller, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { Modal, Select, useTheme, Box, MenuItem, TextField } from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { SecureSubmitButton } from '@components/buttons/SecureSubmitButton';
import { Label, Button } from '@components/index';
import { MemberAutocomplete } from '@pages/customer/common/MemberAutocomplete';
import authStore from '@stores/AuthStore';
import { useStore } from '@stores/RootStore';
import { fonts } from '@theme/fontsCustomer';
import { CalendarStartIcon } from '@icons/CalendarStartIcon';
import { DashIcon } from '@icons/DashIcon';
import { XIcon } from '@icons/XIcon';
import { AddIcon } from '@icons/index';
import { useGetProductPlanList } from '@queryHooks/useSoftware';
import { useGetSubscriptionList } from '@queryHooks/useSubscription';
import { useUpdateSubscriptionUseRequest } from '@queryHooks/useSubscriptionUseRequest';
import { transientOptions } from '@utils/CommonUtil';
import type { SubscriptionUseRequestDetailModel } from '@repositories/subscriptionUseRequestRepository/models/SubscriptionUseRequestDetailModel';
import { StyledTriangleIcon } from './UsageRequestModal';
import { useFormUser } from './validationSchema';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  swId: string;
  modifyData?: SubscriptionUseRequestDetailModel;
}

export const UsageRequestEditModal = ({ isOpen, onClose, swId, modifyData }: Props) => {
  const { t } = useTranslation();
  const {
    uiStore: { toastStore },
  } = useStore();
  const [isStartCalendarOpen, setIsStartCalendarOpen] = useState(false);
  const [isEndCalendarOpen, setIsEndCalendarOpen] = useState(false);

  const { mutate: updateApply, isPending } = useUpdateSubscriptionUseRequest();
  const { data: plans } = useGetProductPlanList(swId, { enabled: isOpen });
  const theme = useTheme();
  const { register, handleSubmit, reset, control, formState, trigger, watch } = useFormUser();
  const {
    fields: approvalSteps,
    remove,
    insert,
  } = useFieldArray({
    control,
    name: 'approvalSteps',
  });
  const [selectedStartDate] = watch(['startDate']);
  const [selectedProductPlanId] = watch(['productPlanId']);

  const selectedProductPlan = plans?.find(plan => plan.planId === selectedProductPlanId);
  const { data: subscriptions } = useGetSubscriptionList(
    { productPlanId: selectedProductPlan?.planId },
    { enabled: isOpen && !!selectedProductPlan },
  );

  const subscriptionList = subscriptions?.content ?? [];

  const hasPlan = !!(plans && plans.length > 0);
  const hasSubscription = !!(subscriptions?.content && subscriptions?.content?.length > 0);
  const hasApprovalSteps = !!(approvalSteps && approvalSteps.length > 0);

  const handleStartCalrendarOpen = () => {
    setIsStartCalendarOpen(!isStartCalendarOpen);
  };
  const handleEndCalrendarOpen = () => {
    setIsEndCalendarOpen(!isEndCalendarOpen);
  };

  const handleDateChange =
    (onChange: UnknownFunction, type: 'startDate' | 'endDate') =>
    (value: string | null, keyboardInputValue?: string) => {
      if (keyboardInputValue && dayjs(keyboardInputValue, 'MM DD, YYYY', true).isValid()) {
        onChange(dayjs(keyboardInputValue));
      } else {
        onChange(value, keyboardInputValue);
      }
      if (type === 'endDate') {
        trigger('startDate');
      }
    };

  const validation = {
    success: (data: FormUseRequest) => {
      const rqData = {
        endDate: dayjs(data.endDate).format('YYYY-MM-DD'),
        inquiry: data.inquiry.replaceAll(' ', '') === '-' ? '' : data.inquiry.trimEnd(),
        productPlanId: data.productPlanId,
        startDate: dayjs(data.startDate).format('YYYY-MM-DD'),
        tenantId: authStore.curTenant.id,
        subscriptionId: data.subscriptionId,
        approvalSteps: data.approvalSteps
          .filter(step => step.approverId)
          .map(step => ({
            approverId: step.approverId ?? '',
          })),
      };

      if (modifyData) {
        updateApply(
          { requestId: modifyData.id, rqData },
          {
            onSuccess: () => {
              toastStore.open({
                message: t('Product_Use_Request_18'),
              });
              onClose();
            },
            onError: () => {
              toastStore.open({
                message: t('Product_Use_Request_20'),
              });
            },
          },
        );
      }
    },
  };

  useEffect(() => {
    if (modifyData) {
      reset({
        startDate: modifyData?.startDate,
        endDate: modifyData?.endDate,
        inquiry: modifyData?.inquiry.replaceAll(' ', '') ? modifyData?.inquiry.trimEnd() : '-',
        productPlanId: modifyData?.productPlanId,
        subscriptionId: modifyData?.subscription?.id ?? '',
        approvalSteps:
          modifyData?.approval?.steps
            ?.sort((a, b) => a.stepOrder - b.stepOrder)
            .map(step => ({
              approverId: step.approverId,
            })) ?? [],
      });
    }
  }, [isOpen, modifyData, reset]);

  const MenuProps = {
    PaperProps: {
      sx: {
        width: 344,
        boxShadow: 'none',
        borderRadius: '0 0 2px 2px',
        left: '50% !important',
        transform: 'translate(-50%, 10px) !important',
      },
    },
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      sx={{ '& .MuiBackdrop-root': { background: `${theme.colors['bg-bk-opacity-40%']}` } }}
    >
      <ModalWrapper onSubmit={handleSubmit(validation.success)}>
        <Header>
          {t('Product_Cardview_01')}
          <IconButton onClick={onClose}>
            <XIcon width={24} height={24} color={theme.colors['ic-gray-main']} />
          </IconButton>
        </Header>
        <Body>
          <Element className="plan-element">
            <strong className="name">
              {t('Subscrib_Main_12')}
              <span>*</span>
            </strong>
            <Controller
              control={control}
              name="productPlanId"
              render={({ field: { onChange, value = '' } }) => (
                <StyledSelect
                  size="large"
                  fullWidth
                  displayEmpty
                  value={value}
                  sx={{ height: '40px' }}
                  onChange={onChange}
                  disabled={!hasPlan}
                  renderValue={value => {
                    const findPlanIdInfor = plans?.find(plan => plan.planId === value);
                    return value !== '' ? (
                      <>
                        <SelectedTitle>{plans?.find(plan => plan.planId === value)?.planName}</SelectedTitle>
                        {findPlanIdInfor?.isInUseByTenant ? (
                          <Label className="usebytenant" variant="contain" color="gradient">
                            {t('Ad_Store_Product_State')}
                          </Label>
                        ) : null}
                      </>
                    ) : (
                      <strong>{hasPlan ? t('Product_Use_Request_02') : t('Product_Use_Request_19')}</strong>
                    );
                  }}
                  MenuProps={MenuProps}
                >
                  {plans?.map(plan => {
                    return (
                      <SelectItem key={plan.planId} value={plan.planId}>
                        <span className="plan-name-wrapper">
                          <span className="plan-name">{plan.planName}</span>
                          {!plan.isBasicPlan && <CustomCard>custom</CustomCard>}
                        </span>
                        {plan.isInUseByTenant ? (
                          <Label variant="contain" color="gradient">
                            {t('Ad_Store_Product_State')}
                          </Label>
                        ) : null}
                      </SelectItem>
                    );
                  })}
                </StyledSelect>
              )}
            />
          </Element>
          <Element>
            <strong className="name">
              {t('Acc_Detail_04')}
              <span>*</span>
            </strong>
            <DateRangeField>
              <Picker>
                <Controller
                  control={control}
                  name="startDate"
                  render={({ field: { onChange, value } }) => {
                    return (
                      <DesktopDatePicker
                        inputFormat={t('DateFormat_YMD')}
                        value={value}
                        onChange={handleDateChange(onChange, 'startDate')}
                        open={isStartCalendarOpen}
                        onClose={() => {
                          setIsStartCalendarOpen(false);
                        }}
                        PaperProps={{
                          sx: {
                            position: 'absolute',
                            top: 14,
                            transform: 'translate(-25%, 0) !important',
                            left: 10,
                            borderRadius: '2px',
                            border: `1px solid ${theme.colors['border-gray-lighter']}`,
                            boxShadow: '0px 1px 6px 0px rgba(0, 0, 0, 0.06)',
                            '& .MuiCalendarOrClockPicker-root': {
                              margin: '10px 0 0',
                            },
                            '& .MuiPickersDay-root': {
                              '&.Mui-selected': {
                                backgroundColor: `${theme.colors['bg-primary-lighter']} !important`,
                                color: `${theme.colors['text-primary-light']}`,
                                '&:hover': {
                                  backgroundColor: `${theme.colors['state-white-primary-hover']}`,
                                },
                              },
                              '&:hover': {
                                backgroundColor: `${theme.colors['state-white-primary-hover']}`,
                              },
                            },
                          },
                        }}
                        showDaysOutsideCurrentMonth
                        renderInput={params => (
                          <DatePickerTextField
                            {...params}
                            size="large"
                            fullWidth
                            error={!!formState.errors.startDate}
                            inputProps={{
                              ...params.inputProps,
                              placeholder: t('DateFormat_YMD') === 'YYYY.MM.DD' ? 'YYYY.MM.DD' : 'MM DD,YYYY',
                            }}
                            InputProps={{
                              endAdornment: (
                                <Button variant="text" onClick={handleStartCalrendarOpen}>
                                  <CalendarStartIcon
                                    width={16}
                                    height={16}
                                    color={
                                      isStartCalendarOpen
                                        ? theme.colors['ic-primary-light']
                                        : theme.colors['ic-gray-dark']
                                    }
                                  />
                                </Button>
                              ),
                            }}
                            $isStartCalendarOpen={isStartCalendarOpen}
                          />
                        )}
                      />
                    );
                  }}
                />
                <DashIcon width={10} height={10} color={theme.colors['ic-gray-dark']} />
                <Controller
                  control={control}
                  name="endDate"
                  render={({ field: { onChange, value } }) => (
                    <DesktopDatePicker
                      inputFormat={t('DateFormat_YMD')}
                      value={value}
                      onChange={handleDateChange(onChange, 'endDate')}
                      open={isEndCalendarOpen}
                      onClose={() => {
                        setIsEndCalendarOpen(false);
                      }}
                      showDaysOutsideCurrentMonth
                      shouldDisableDate={day => {
                        const disableDate = dayjs(selectedStartDate).add(1, 'D');
                        return dayjs.isDayjs(day) ? day.isBefore(disableDate) : false;
                      }}
                      PaperProps={{
                        sx: {
                          position: 'absolute',
                          top: 14,
                          transform: 'translate(-25%, 0) !important',
                          left: 10,
                          borderRadius: '2px',
                          border: `1px solid ${theme.colors['border-gray-lighter']}`,
                          boxShadow: '0px 1px 6px 0px rgba(0, 0, 0, 0.06)',
                          '& .MuiCalendarOrClockPicker-root': {
                            margin: '10px 0 0',
                          },
                          '& .MuiPickersDay-root': {
                            '&.Mui-selected': {
                              backgroundColor: `${theme.colors['bg-primary-lighter']} !important`,
                              color: `${theme.colors['text-primary-light']}`,
                              '&:hover': {
                                backgroundColor: `${theme.colors['state-white-primary-hover']}`,
                              },
                            },
                            '&:hover': {
                              backgroundColor: `${theme.colors['state-white-primary-hover']}`,
                            },
                          },
                        },
                      }}
                      renderInput={params => (
                        <DatePickerTextField
                          {...params}
                          size="large"
                          fullWidth
                          error={!!formState.errors.endDate || formState.errors.startDate?.message === t('Date_02')}
                          inputProps={{
                            ...params.inputProps,
                            placeholder: t('DateFormat_YMD') === 'YYYY.MM.DD' ? 'YYYY.MM.DD' : 'MM DD,YYYY',
                          }}
                          InputProps={{
                            endAdornment: (
                              <Button variant="text" onClick={handleEndCalrendarOpen}>
                                <CalendarStartIcon
                                  width={16}
                                  height={16}
                                  color={
                                    isEndCalendarOpen ? theme.colors['ic-primary-light'] : theme.colors['ic-gray-dark']
                                  }
                                />
                              </Button>
                            ),
                          }}
                          $isEndCalendarOpen={isEndCalendarOpen}
                        />
                      )}
                    />
                  )}
                />
              </Picker>
              <p className="detail">{t('Product_Use_Request_06')}</p>
              {formState.errors.startDate || formState.errors.endDate ? (
                <ErrorTextField>
                  <ErrorText>{formState.errors.startDate?.message}</ErrorText>
                  <ErrorText>{formState.errors.endDate?.message}</ErrorText>
                </ErrorTextField>
              ) : null}
            </DateRangeField>
          </Element>
          <Element>
            <strong className="name">{t('Product_Use_Request_14')}</strong>
            <Controller
              control={control}
              name="subscriptionId"
              render={({ field: { onChange, value = '' } }) => (
                <StyledSelect
                  size="large"
                  fullWidth
                  displayEmpty
                  value={value}
                  sx={{ height: '40px' }}
                  IconComponent={StyledTriangleIcon}
                  disabled={!hasSubscription}
                  onChange={onChange}
                  renderValue={value => {
                    return value !== '' ? (
                      <SelectedTitle>
                        {subscriptionList.find(subscription => subscription.id === value)?.name}
                      </SelectedTitle>
                    ) : (
                      <strong>{hasSubscription ? t('Product_Use_Request_15') : t('Product_Use_Request_21')}</strong>
                    );
                  }}
                  MenuProps={MenuProps}
                >
                  {subscriptionList.map(subscription => {
                    return (
                      <SelectItem key={subscription.id} value={subscription.id}>
                        <span className="plan-name-wrapper">
                          <span className="plan-name">{subscription.name}</span>
                        </span>
                      </SelectItem>
                    );
                  })}
                </StyledSelect>
              )}
            />
          </Element>
          {approvalSteps.length > 0 && (
            <Element>
              <strong className="name">{t('Product_Use_Request_16')}</strong>
              <ApprovalStepSection>
                {approvalSteps.map((step, index) => {
                  // 현재 행을 제외한 다른 행에서 이미 선택된 승인자 ID 목록
                  const selectedApproverIds = approvalSteps
                    .filter((_, idx) => idx !== index)
                    .map(step => step.approverId)
                    .filter((id): id is string => !!id);

                  return (
                    <Controller
                      key={step.id}
                      control={control}
                      name={`approvalSteps.${index}.approverId`}
                      render={({ field: { onChange, value } }) => {
                        return (
                          <InputSection>
                            <MemberAutocomplete
                              value={value || null}
                              onChange={v => onChange(v || '')}
                              placeholder={t('Product_Use_Request_17')}
                              fullWidth
                              getOptionLabel={option =>
                                option.usergroups?.length > 0 ? `${option.name} (${option.usergroups})` : option.name
                              }
                              getOptionDisabled={option => {
                                const isDisabled = selectedApproverIds.includes(option.id);
                                return isDisabled;
                              }}
                              disabled={!hasApprovalSteps}
                              paperWidth="248px"
                              listboxHeight="auto"
                            />
                            <IconButton2
                              onClick={() => insert(index + 1, { approverId: '' })}
                              variant="outline"
                              size="small"
                              disabled={approvalSteps.length >= 4}
                            >
                              <AddIcon width={24} height={24} />
                            </IconButton2>
                            <IconButton2
                              onClick={() => remove(index)}
                              variant="outline"
                              size="small"
                              disabled={approvalSteps.length <= 1}
                            >
                              <XIcon width={24} height={24} />
                            </IconButton2>
                          </InputSection>
                        );
                      }}
                    />
                  );
                })}
              </ApprovalStepSection>
            </Element>
          )}

          <Element>
            <strong className="name">
              {t('Product_Use_Request_07')}
              <span>*</span>
            </strong>
            <StyledTextField
              {...register('inquiry')}
              multiline
              rows={9}
              autoFocus
              placeholder={t('Product_Use_Request_08')}
            />
          </Element>
        </Body>
        <SubmitButton isPending={isPending} variant="contain" size="extraLarge" type="submit">
          {t('Ad_Store_Purchase_Request_03')}
        </SubmitButton>
      </ModalWrapper>
    </Modal>
  );
};

const ModalWrapper = styled.form`
  position: absolute;
  bottom: 20px;
  right: 20px;
  width: 400px;
  height: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  bottom: 50%;
  right: 50%;
  transform: translate(50%, 50%);
  background-color: ${({ theme: { colors } }) => colors['bg-white']};
  border-radius: 6px;
  overflow: hidden;
  z-index: 1050;
  box-shadow: 0px 8px 10px -5px rgba(0, 0, 0, 0.08), 0px 16px 24px 2px rgba(0, 0, 0, 0.04),
    0px 6px 30px 5px rgba(0, 0, 0, 0.05);
`;

const Header = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: inherit;
  padding: 18px 20px 16px 28px;
  ${fonts.Headline6};
  color: ${({ theme: { colors } }) => colors['text-gray-main']};
`;

const Body = styled(Box)`
  display: flex;
  flex-direction: column;
  padding: 12px 28px 0;
  width: 100%;
  & .plan-element {
    & .MuiInputBase-root {
      padding: 6px 8px 6px 12px;
    }
  }
  max-height: 508px;
  overflow: auto;
`;

const Element = styled(Box)`
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
  & .name {
    ${fonts.Headline8};
    margin-bottom: 4px;
    color: ${({ theme: { colors } }) => colors['text-gray-main']};
    & > span {
      margin-left: 2px;
      color: ${({ theme: { colors } }) => colors['ic-red-light']};
    }
  }
  & .detail {
    ${fonts.Body4};
    color: ${({ theme: { colors } }) => colors['text-gray-light']};
  }
  &:last-of-type {
    margin-bottom: 28px;
  }
  & .MuiInputBase-root {
    padding: 10px 12px;
  }
  & .MuiInputBase-root textarea::placeholder {
    ${fonts.Body2};
    color: ${({ theme }) => theme.colors['text-gray-light']};
  }
  & .MuiSelect-select strong {
    ${fonts.Button4};
    color: ${({ theme }) => theme.colors['text-gray-main']};
  }
`;

const StyledTextField = styled(TextField)`
  background: ${({ theme }) => theme.colors['bg-gray-lighter']};
  color: red;
  & .MuiInputBase-multiline textarea {
    height: 198px !important;
  }
`;

const DateRangeField = styled(Box)`
  display: flex;
  flex-direction: column;
`;

const Picker = styled(Box)`
  display: flex;
  align-items: center;
  gap: 7px;
  margin-bottom: 4px;
  & > svg {
    flex-shrink: 0;
  }
  & > .MuiFormControl-root {
    width: 50%;
  }
`;

const ErrorText = styled('p')`
  ${fonts.Body4};
  color: ${({ theme }) => theme.colors['text-red-light']};
  &.plan {
    margin-top: 4px;
  }
`;

const SubmitButton = styled(SecureSubmitButton)`
  width: calc(100% - 56px);
  margin: 0 28px 28px;
  padding: 8px 24px;
  border-radius: 3px;
  background: ${({ theme }) => theme.colors['bg-primary']};
  border-image-slice: 1;
`;

const SelectItem = styled(MenuItem)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 23px;
  padding: 10px 4px 10px 7px;
  align-items: center;
  max-width: 414px;
  & .plan-name-wrapper {
    display: flex;
    flex-direction: row;
    gap: 2px;
    align-items: center;
    max-width: 314px;
    & .plan-name {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
`;

const CustomCard = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding: 0px 5px;
  ${fonts.Caption4}
  color: ${props => props.theme.colors['text-gray-sub-darker']};
  border: 1px solid ${props => props.theme.colors['border-gray-lighter']};
  border-radius: 2px;
`;

const IconButton = styled(Button)`
  width: 24px;
  height: 24px;
`;

const SelectedTitle = styled.strong`
  & ~ span {
    margin-left: 4px;
  }
`;
const ErrorTextField = styled.div`
  display: flex;
  position: relative;

  & p + p {
    position: absolute;
    left: 184px;
  }
`;
const DatePickerTextField = styled(TextField, transientOptions)<{
  $isStartCalendarOpen?: boolean;
  $isEndCalendarOpen?: boolean;
}>`
  & .MuiInputBase-root {
    padding: 10px 12px;
    & .MuiInputBase-input {
      height: auto;
      padding: 0;
      ${fonts.Body2};
    }
    & .MuiOutlinedInput-notchedOutline {
      border-color: ${({ $isStartCalendarOpen, $isEndCalendarOpen, theme: { colors } }) =>
        $isStartCalendarOpen
          ? colors['ic-primary-light']
          : $isEndCalendarOpen
          ? colors['ic-primary-light']
          : colors['border-gray-light']};
    }
  }
`;

const ApprovalStepSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const InputSection = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const IconButton2 = styled(Button)`
  width: 40px;
  height: 40px;
`;

const StyledSelect = styled(Select)`
  &.Mui-disabled {
    background-color: ${({ theme: { colors } }) => colors['bg-gray-light']};
    cursor: not-allowed;
  }
  & .MuiSelect-select strong {
    ${fonts.Button4};
    color: ${({ theme }) => theme.colors['text-gray-main']};
  }
`;
